/**
 * El aviso de versión nueva, arriba del panel (13-oct-2026). Rais: «al principio tendremos que hacer bastantes releases».
 *
 * Antes era una línea pequeña al final de Ajustes con un enlace. Ahora se ve en cuanto se abre el panel y, si el programa
 * puede, un botón: «Actualizar» baja el instalador, comprueba que es el que se publicó y lo abre. No instala nada solo.
 * No habla con Word. Pregunta al motor una vez al abrir, y solo sigue preguntando mientras hay una descarga en marcha.
 */
import { useCallback, useEffect, useState } from "react";
import { COMPANION } from "./companion";
import { hayQueAvisar, enMarcha, titularDe, botonDe, fraseDelEstado, rotuloDe, hayQueOfrecerloAMano, FRASE_A_MANO, FRASE_SIN_MOTOR, type Actualizacion } from "./actualizarLogica";

export type PuenteDeActualizar = { mirar: () => Promise<Actualizacion | null>; actualizar: () => Promise<Actualizacion | null> };
const DE_WORD = { "x-hi-cliente": "word" };
const alMotor = async (metodo: "GET" | "POST"): Promise<Actualizacion | null> => {
  // (El motor solo admite un POST que diga que es JSON: es su defensa contra formularios de otras webs.)
  const r = await fetch(`${COMPANION}/actualizacion`, metodo === "POST" ? { method: "POST", headers: { ...DE_WORD, "content-type": "application/json" }, body: "{}" } : { headers: DE_WORD });
  const d = await r.json().catch(() => null);
  // Un motor anterior no tiene esta ruta: no hay aviso.
  return d && typeof d === "object" && "estado" in d ? (d as Actualizacion) : null;
};
const PUENTE: PuenteDeActualizar = { mirar: () => alMotor("GET"), actualizar: () => alMotor("POST") };
const CADA = 1500;

export default function Actualizar({ puente = PUENTE }: { puente?: PuenteDeActualizar }) {
  const [a, setA] = useState<Actualizacion | null>(null);
  const [pidiendo, setPidiendo] = useState(false);
  // Cuántas veces seguidas no ha contestado el motor. Sin esto, si se reiniciaba a media descarga el panel se quedaba
  // para siempre en «Bajando: 49 %»: la pregunta fallaba, nada cambiaba y no se volvía a preguntar (equipo rojo, 13-oct).
  const [sinMotor, setSinMotor] = useState(0);
  const mirar = useCallback(async () => { try { setA(await puente.mirar()); setSinMotor(0); } catch { setSinMotor((n) => n + 1); } }, [puente]);
  useEffect(() => { void mirar(); }, [mirar]);
  useEffect(() => {
    if (!enMarcha(a)) return;
    const t = setTimeout(() => void mirar(), CADA);
    return () => clearTimeout(t);
  }, [a, mirar, sinMotor]);

  if (!hayQueAvisar(a)) return null;
  const perdido = enMarcha(a) && sinMotor >= 3;
  const boton = botonDe(a), estado = perdido ? FRASE_SIN_MOTOR : fraseDelEstado(a), mal = perdido || a.estado.fase === "fallo" || a.estado.fase === "lista";
  const pulsar = async () => {
    if (boton === "a_mano") { window.open(a.enlace, "_blank", "noopener"); return; }
    setPidiendo(true);
    try { const d = await puente.actualizar(); if (d) setA(d); } catch { /* se queda como estaba */ } finally { setPidiendo(false); }
  };
  return (
    <section aria-label="Versión nueva" style={{ margin: "0 0 12px", padding: "10px 12px", borderRadius: 8, background: "var(--hi-info-fondo)", border: "1px solid var(--hi-info-raya)", color: "var(--hi-info)", fontSize: 12, lineHeight: 1.45 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <b style={{ fontSize: 13 }}>{titularDe(a)}</b>
        {boton && (
          <button type="button" onClick={() => void pulsar()} disabled={pidiendo}
            style={{ flexShrink: 0, border: "1px solid var(--hi-info-2)", background: "transparent", color: "var(--hi-info)", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 700, cursor: pidiendo ? "default" : "pointer" }}>
            {rotuloDe(boton)}
          </button>
        )}
      </div>
      {a.ultima?.notas && <p style={{ margin: "6px 0 0" }}>{a.ultima.notas}</p>}
      {!a.se_puede && <p style={{ margin: "6px 0 0" }}>{FRASE_A_MANO}</p>}
      {estado && <p role="status" style={{ margin: "6px 0 0", color: mal ? "var(--hi-mal-suave)" : "var(--hi-info)", fontWeight: 600 }}>{estado}</p>}
      {(perdido || hayQueOfrecerloAMano(a)) && <p style={{ margin: "4px 0 0" }}>También puedes <a href={a.enlace} target="_blank" rel="noopener" style={{ color: "var(--hi-info-2)" }}>descargarlo a mano</a>.</p>}
    </section>
  );
}
